import { useTestAutomationRule } from '@servicetim/ui/api';
import { Badge, Button, Input, ProblemBanner } from '@servicetim/ui/components';
import { useState } from 'react';
import { FormRow } from '../projects/ui/form-row';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** S-07-12: dry-run the rule against one ticket — evaluates trigger/conditions, applies nothing. */
export function RuleTestPanel({ ruleId }: { ruleId: string }) {
  const [ticketId, setTicketId] = useState('');
  const test = useTestAutomationRule(ruleId);

  return (
    <div className="space-y-4">
      <p className="text-fg-muted text-sm">
        Evaluates the trigger's filter (for an <code className="font-mono">event</code> trigger) and
        every condition against the ticket below, and shows which actions would run — nothing is
        applied.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <FormRow label="Ticket id" htmlFor="rule-test-ticket-id" className="max-w-md flex-1">
          <Input
            id="rule-test-ticket-id"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value)}
            placeholder="Paste the ticket's id (UUID)"
            className="font-mono text-xs"
          />
        </FormRow>
        <Button
          type="button"
          disabled={!UUID_PATTERN.test(ticketId) || test.isPending}
          onClick={() => test.mutate({ ticketId })}
        >
          Run test
        </Button>
      </div>
      {test.error ? <ProblemBanner error={test.error} size="sm" /> : null}
      {test.data ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-medium text-fg-muted text-sm">Trigger matched</span>
            <Badge variant={test.data.triggerMatched ? 'success' : 'secondary'}>
              {test.data.triggerMatched ? 'Yes' : 'No'}
            </Badge>
          </div>
          {test.data.conditions.length > 0 ? (
            <div className="space-y-1.5">
              <span className="font-medium text-fg-muted text-sm">Conditions</span>
              <ul className="space-y-1">
                {test.data.conditions.map((c, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: conditions carry no identity
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <Badge variant={c.result ? 'success' : 'destructive'}>
                      {c.result ? 'pass' : 'fail'}
                    </Badge>
                    <code className="truncate font-mono text-fg-muted">
                      {JSON.stringify(c.rule)}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <span className="font-medium text-fg-muted text-sm">
              Planned actions ({test.data.plannedActions.length})
            </span>
            {test.data.plannedActions.length === 0 ? (
              <p className="text-fg-muted text-xs">
                Nothing would run (the trigger did not match, or a condition failed).
              </p>
            ) : (
              <pre className="max-h-64 overflow-auto rounded-md border border-border bg-surface-sunken p-3 text-xs">
                {JSON.stringify(test.data.plannedActions, null, 2)}
              </pre>
            )}
          </div>
          {test.data.problems.length > 0 ? (
            <pre className="overflow-auto rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-red-700 dark:text-red-300 text-xs">
              {JSON.stringify(test.data.problems, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
